import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellRing, BellOff, Mail, MessageSquareText, RefreshCw, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { getCurrentSubscription, pushSupported, subscribePush, unsubscribePush } from "@/lib/push";
import { Switch } from "@/components/ui/switch";

const CHANNEL_STYLE = {
  push: { label: "Notification", color: "text-copper" },
  email: { label: "E-mail", color: "text-gold" },
  sms: { label: "SMS", color: "text-ivory" },
};

const STATUS_STYLE = {
  sent: "text-gold",
  failed: "text-red-400",
  disabled: "text-ivory/40",
};

export default function NotificationsManager() {
  const [cfg, setCfg] = useState(null);
  const [log, setLog] = useState([]);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = () => {
    api.get("/admin/notifications/settings").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/admin/notifications/log").then((r) => setLog(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    getCurrentSubscription().then((s) => setSubscribed(!!s)).catch(() => {});
  }, []);

  const enablePush = async () => {
    setBusy(true);
    try {
      await subscribePush();
      setSubscribed(true);
      toast.success("Notifications activées sur cet appareil.");
    } catch (e) {
      toast.error(e.message || "Activation impossible.");
    } finally {
      setBusy(false);
    }
  };

  const disablePush = async () => {
    setBusy(true);
    try {
      await unsubscribePush();
      setSubscribed(false);
      toast.success("Notifications désactivées sur cet appareil.");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/push/test");
      if (data.sent > 0) toast.success(`Notification de test envoyée (${data.sent} appareil(s)).`);
      else toast.error("Aucun appareil abonné — activez d'abord les notifications sur ce téléphone.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/notifications/settings", cfg);
      setCfg(data);
      toast.success("Réglages de notification enregistrés.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <p className="text-sm text-ivory/40">Chargement…</p>;

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  return (
    <div data-testid="notifications-manager">
      <h2 className="mb-2 text-lg font-semibold text-ivory">Notifications de réservation</h2>
      <p className="mb-8 max-w-2xl text-xs leading-relaxed text-ivory/40">
        Recevez une alerte immédiate sur votre téléphone à chaque nouvelle réservation, puis une
        relance automatique tant qu'elle n'est pas traitée. Sur iPhone : ajoutez d'abord le site à
        l'écran d'accueil (Partager → Sur l'écran d'accueil), rouvrez-le depuis l'icône, puis activez
        les notifications ci-dessous.
      </p>

      <div className="grid gap-8 lg:grid-cols-2">
        <div className="space-y-8">
          <div className="space-y-5 rounded-lg border border-white/10 bg-coal/60 p-8" data-testid="push-device-panel">
            <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">Cet appareil</h3>
            <div className="flex flex-wrap items-center gap-3">
              {!subscribed ? (
                <button onClick={enablePush} disabled={busy || !pushSupported()} className="btn-primary" data-testid="push-enable-button">
                  <BellRing className="h-4 w-4" strokeWidth={1.5} />
                  Activer les notifications sur ce téléphone
                </button>
              ) : (
                <>
                  <span className="flex items-center gap-2 rounded-md border border-gold/40 px-4 py-2 text-xs font-semibold text-gold" data-testid="push-subscribed-badge">
                    <Bell className="h-3.5 w-3.5" strokeWidth={1.5} />
                    Notifications actives sur cet appareil
                  </span>
                  <button onClick={disablePush} disabled={busy} className="btn-outline !h-10 !px-4 !text-[11px]" data-testid="push-disable-button">
                    <BellOff className="h-3.5 w-3.5" strokeWidth={1.5} />
                    Désactiver
                  </button>
                </>
              )}
              <button onClick={sendTest} disabled={testing} className="btn-outline !h-10 !px-4 !text-[11px]" data-testid="push-test-button">
                <Send className="h-3.5 w-3.5" strokeWidth={1.5} />
                {testing ? "Envoi…" : "Envoyer une notification de test"}
              </button>
            </div>
            {!pushSupported() && (
              <p className="text-xs text-copper" data-testid="push-unsupported">
                Ce navigateur ne supporte pas les notifications push. Sur iPhone, utilisez Safari et
                ajoutez le site à l'écran d'accueil.
              </p>
            )}
          </div>

          <div className="space-y-5 rounded-lg border border-white/10 bg-coal/60 p-8" data-testid="channels-panel">
            <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">Canaux d'alerte</h3>
            {[
              { key: "push_enabled", label: "Notification sur le téléphone", icon: Bell },
              { key: "email_enabled", label: "E-mail de secours", icon: Mail },
              { key: "sms_enabled", label: "SMS d'urgence (réservation à moins de 4 h)", icon: MessageSquareText },
            ].map((c) => (
              <label key={c.key} className="flex items-center justify-between gap-4 text-sm text-ivory/80">
                <span className="flex items-center gap-3">
                  <c.icon className="h-4 w-4 text-ivory/40" strokeWidth={1.5} />
                  {c.label}
                </span>
                <Switch checked={!!cfg[c.key]} onCheckedChange={(v) => set(c.key, v)} data-testid={`channel-${c.key}`} />
              </label>
            ))}
            <div className="grid gap-4 pt-2 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">E-mail destinataire</label>
                <input value={cfg.admin_email} onChange={(e) => set("admin_email", e.target.value)} placeholder="Par défaut : e-mail du propriétaire" className="input-dark !h-11" data-testid="notif-admin-email" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Téléphone SMS (ex : +33612345678)</label>
                <input value={cfg.admin_phone} onChange={(e) => set("admin_phone", e.target.value)} placeholder="+33…" className="input-dark !h-11" data-testid="notif-admin-phone" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Nom d'expéditeur affiché</label>
                <input value={cfg.email_sender_name || ""} onChange={(e) => set("email_sender_name", e.target.value)} placeholder="Par défaut : Palais Maasaï" className="input-dark !h-11" data-testid="notif-sender-name" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Adresse de réponse (Reply-To)</label>
                <input value={cfg.email_reply_to || ""} onChange={(e) => set("email_reply_to", e.target.value)} placeholder="Ex : lepmaasai@gmail.com" className="input-dark !h-11" data-testid="notif-reply-to" />
              </div>
            </div>
            <p className="text-xs leading-relaxed text-ivory/40">
              L'adresse d'expédition technique est gérée par la plateforme d'envoi (domaine vérifié,
              délivrabilité optimale) et ne peut pas être remplacée par une adresse @palaismaasai.fr
              sans compte d'envoi dédié. Le nom affiché et l'adresse de réponse, eux, sont modifiables
              ci-dessus. Pour une future adresse professionnelle : reservations@palaismaasai.fr.
            </p>
          </div>

          <div className="space-y-5 rounded-lg border border-white/10 bg-coal/60 p-8" data-testid="reminders-panel">
            <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">Relances automatiques</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Délai entre relances (minutes)</label>
                <input type="number" min="1" max="120" value={cfg.reminder_delay_min} onChange={(e) => set("reminder_delay_min", Number(e.target.value))} className="input-dark !h-11" data-testid="notif-reminder-delay" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Nombre maximal de relances</label>
                <input type="number" min="0" max="20" value={cfg.max_reminders} onChange={(e) => set("max_reminders", Number(e.target.value))} className="input-dark !h-11" data-testid="notif-max-reminders" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Alertes à partir de (heure)</label>
                <input type="number" min="0" max="23" value={cfg.hours_start} onChange={(e) => set("hours_start", Number(e.target.value))} className="input-dark !h-11" data-testid="notif-hours-start" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Alertes jusqu'à (heure)</label>
                <input type="number" min="0" max="24" value={cfg.hours_end} onChange={(e) => set("hours_end", Number(e.target.value))} className="input-dark !h-11" data-testid="notif-hours-end" />
              </div>
            </div>
            <p className="text-xs text-ivory/40">
              Les relances s'arrêtent dès que la réservation est acceptée, refusée ou annulée.
            </p>
          </div>

          <div className="space-y-5 rounded-lg border border-white/10 bg-coal/60 p-8" data-testid="ovh-panel">
            <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">SMS OVHcloud (optionnel)</h3>
            <p className="text-xs leading-relaxed text-ivory/40">
              {cfg.ovh_configured
                ? "Identifiants OVH enregistrés. Laissez les champs vides pour les conserver, ou saisissez de nouvelles valeurs."
                : "Sans identifiants OVH, aucun SMS n'est envoyé (aucun coût). Créez vos identifiants sur auth.eu.ovhcloud.com/api/createToken puis collez-les ici."}
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <input value={cfg.ovh_app_key} onChange={(e) => set("ovh_app_key", e.target.value)} placeholder="Application Key" className="input-dark !h-11" data-testid="ovh-app-key" />
              <input value={cfg.ovh_service_name} onChange={(e) => set("ovh_service_name", e.target.value)} placeholder="Service Name (sms-…)" className="input-dark !h-11" data-testid="ovh-service-name" />
              <input type="password" value={cfg.ovh_app_secret} onChange={(e) => set("ovh_app_secret", e.target.value)} placeholder="Application Secret" className="input-dark !h-11" data-testid="ovh-app-secret" />
              <input type="password" value={cfg.ovh_consumer_key} onChange={(e) => set("ovh_consumer_key", e.target.value)} placeholder="Consumer Key" className="input-dark !h-11" data-testid="ovh-consumer-key" />
            </div>
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="notif-save-button">
              {saving ? "Enregistrement…" : "Enregistrer les réglages"}
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  const { data } = await api.post("/admin/notifications/test-sms");
                  if (data.status === "sent") toast.success("SMS de test envoyé.");
                  else if (data.status === "disabled") toast.error("Canal SMS inactif : activez-le et renseignez les identifiants OVH + numéro, puis enregistrez.");
                  else toast.error(`SMS échoué : ${data.detail || "erreur OVH"}`);
                  load();
                } catch {
                  toast.error("Envoi du SMS de test impossible.");
                }
              }}
              className="btn-outline !h-11 !px-5 !text-[11px]"
              data-testid="sms-test-button"
            >
              <MessageSquareText className="h-4 w-4" strokeWidth={1.5} />
              Envoyer un SMS de test
            </button>
          </div>
        </div>

        <div className="rounded-lg border border-white/10 bg-coal/60 p-8" data-testid="notification-log-panel">
          <div className="mb-5 flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">Journal des notifications</h3>
            <button onClick={load} aria-label="Rafraîchir" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/50 hover:border-gold hover:text-gold" data-testid="notif-log-refresh">
              <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          </div>
          {log.length === 0 ? (
            <p className="text-sm text-ivory/40" data-testid="notif-log-empty">Aucune notification envoyée pour le moment.</p>
          ) : (
            <div className="space-y-2" data-testid="notif-log-list">
              {log.map((n) => (
                <div key={n.id} className="rounded-md border border-white/10 px-4 py-3" data-testid={`notif-log-${n.id}`}>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className={`text-[11px] font-semibold uppercase tracking-wider ${CHANNEL_STYLE[n.channel]?.color || "text-ivory"}`}>
                      {CHANNEL_STYLE[n.channel]?.label || n.channel}
                    </span>
                    <span className={`text-[11px] font-semibold uppercase ${STATUS_STYLE[n.status] || "text-ivory/50"}`}>
                      {n.status === "sent" ? "Envoyée" : n.status === "failed" ? "Échouée" : "Inactive"}
                    </span>
                    <span className="text-[11px] text-ivory/30">{new Date(n.created_at).toLocaleString("fr-FR")}</span>
                  </div>
                  <p className="mt-1 truncate text-xs text-ivory/60">{n.title}</p>
                  {n.detail && <p className="truncate text-[11px] text-ivory/35">{n.detail}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
